<template>
    <div class="token-detail-tabs">
        <!-- Tab Navigation -->
        <div class="detail-tabs-nav">
            <div
                v-for="tab in tabs"
                :key="tab.id"
                class="detail-tab"
                :class="{ 'active': activeTab === tab.id }"
                @click="activeTab = tab.id"
            >
                {{ tab.label }}
            </div>
        </div>

        <!-- Tab Content -->
        <div class="detail-tabs-content">
            <div v-show="activeTab === 'trades'" class="tab-pane">
                <div class="card compass-styled mb-4">
                    <div class="card-header">
                        <h5 class="card-title mb-0">Live Trades</h5>
                    </div>
                    <div class="card-body p-0 table-mobile-scroll">
                        <token-trades :mint="mint"></token-trades>
                    </div>
                </div>
            </div>

            <div v-show="activeTab === 'pools'" class="tab-pane">
                <div class="card compass-styled mb-4">
                    <div class="card-header">
                        <h5 class="card-title mb-0">Liquidity Pools</h5>
                    </div>
                    <div class="card-body p-0 table-mobile-scroll">
                        <token-markets :mint="mint"></token-markets>
                    </div>
                </div>
            </div>

            <div v-show="activeTab === 'technical'" class="tab-pane">
                <div class="mb-4">
                    <token-technical-analysis
                        :mint="mint"
                        :initial-analysis="initialAnalysis"
                    ></token-technical-analysis>
                </div>
            </div>

            <div v-show="activeTab === 'holders'" class="tab-pane">
                <div class="holders-subtabs">
                    <div
                        class="holders-subtab"
                        :class="{ active: holdersSubTab === 'list' }"
                        @click="holdersSubTab = 'list'"
                    >List</div>
                    <div
                        class="holders-subtab"
                        :class="{ active: holdersSubTab === 'analysis' }"
                        @click="holdersSubTab = 'analysis'"
                    >Analysis</div>
                </div>
                <div v-show="holdersSubTab === 'list'" class="mb-4">
                    <holder-analytics :mint="mint"></holder-analytics>
                </div>
                <div v-show="holdersSubTab === 'analysis'" class="mb-4">
                    <holder-analytics-charts :mint="mint"></holder-analytics-charts>
                </div>
            </div>

            <!-- Supply tab disabled until token-events index is populated
            <div v-show="activeTab === 'supply'" class="tab-pane">
                <div class="mb-4">
                    <token-supply-events
                        :mint="mint"
                        :token-symbol="tokenSymbol"
                        :decimals="decimals"
                        :current-supply="currentSupply"
                    ></token-supply-events>
                </div>
            </div>
            -->
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';

const props = defineProps({
    mint: {
        type: String,
        required: true,
    },
    initialAnalysis: {
        type: Object,
        default: null,
    },
    tokenSymbol: {
        type: String,
        default: '',
    },
    decimals: {
        type: Number,
        default: null,
    },
    currentSupply: {
        type: [Number, String],
        default: null,
    },
});

const tabs = [
    { id: 'trades', label: 'Trades' },
    { id: 'pools', label: 'Liquidity Pools' },
    { id: 'technical', label: 'Technical Analysis' },
    { id: 'holders', label: 'Holders' },
    // { id: 'supply', label: 'Supply' }, // Disabled until token-events index is populated
];

const activeTab = ref('trades');
const holdersSubTab = ref('list');
</script>

<style scoped>
.token-detail-tabs {
    width: 100%;
}

/* Tab Navigation - matching TokenStats time-tab style */
.detail-tabs-nav {
    display: flex;
    gap: 0.15rem;
    margin-bottom: 1rem;
    background: rgba(139, 92, 246, 0.08);
    border-radius: 8px;
    padding: 0.2rem;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
}

.detail-tabs-nav::-webkit-scrollbar {
    display: none;
}

.detail-tab {
    flex: 0 0 auto;
    padding: 0.5rem 1rem;
    border: none;
    background: transparent;
    color: var(--compass-text-secondary, #7d8590);
    font-size: 0.8rem;
    font-weight: 600;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    white-space: nowrap;
    text-align: center;
}

.detail-tab:hover:not(.active) {
    color: var(--compass-text-primary, #e6edf3);
    background: rgba(139, 92, 246, 0.1);
}

.detail-tab.active {
    background: linear-gradient(135deg, var(--compass-purple, #8b5cf6), var(--compass-pink, #ec4899));
    color: #ffffff;
    font-weight: 700;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

/* Tab Content */
.detail-tabs-content {
    width: 100%;
}

.tab-pane {
    width: 100%;
}

/* Light mode */
body:not(.dark-mode) .detail-tabs-nav {
    background: rgba(139, 92, 246, 0.05);
}

body:not(.dark-mode) .detail-tab {
    color: #6b7280;
}

body:not(.dark-mode) .detail-tab:hover:not(.active) {
    color: #374151;
    background: rgba(139, 92, 246, 0.08);
}

/* IMPORTANT: Keep white text on gradient active state in light mode */
body:not(.dark-mode) .detail-tab.active {
    color: #ffffff;
}

/* Holders sub-tabs */
.holders-subtabs {
    display: flex;
    gap: 0;
    margin-bottom: 1rem;
    border-bottom: 1px solid var(--compass-border, rgba(139, 92, 246, 0.15));
}

.holders-subtab {
    padding: 0.5rem 1.25rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--compass-text-secondary, #7d8590);
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: all 0.2s ease;
}

.holders-subtab:hover:not(.active) {
    color: var(--compass-text-primary, #e6edf3);
}

.holders-subtab.active {
    color: var(--compass-purple, #8b5cf6);
    border-bottom-color: var(--compass-purple, #8b5cf6);
}

body:not(.dark-mode) .holders-subtab {
    color: #6b7280;
}

body:not(.dark-mode) .holders-subtab.active {
    color: var(--compass-purple, #8b5cf6);
}

/* Mobile responsive */
@media (max-width: 768px) {
    .detail-tab {
        padding: 0.4rem 0.75rem;
        font-size: 0.75rem;
    }
}
</style>
